<template>
  <div class="e4s-flex-row">
    <div class="e4s-flex-column e4s-justify-flex-end e4s-header--500">Bib</div>
    <InputDebounce
      class="e4s-flex-row--end"
      placeholder="Search..."
      :value="filterTerm"
      @input="filterTeams"
    />
  </div>
  <hr class="dat-e4s-hr-only dat-e4s-hr--light" />

  <!-- SEEDED_TEAMS -->
  <div
    v-for="(teams, heatNo) in teamsGroupedByHeatNo"
    :key="heatNo"
    class="e4s-flex-column-x"
  >
    <div class="e4s-vertical-spacer--standard"></div>
    <div class="e4s-flex-column e4s-lite--header-race-heat">
      <div class="e4s-flex-row">
        <div v-if="isTrack ? true : howManyHeats > 1" class="e4s-header--400">
          <span v-text="isTrack ? 'Race' : 'Heat'"></span>
          {{ heatNo }}
        </div>
      </div>

      <div class="e4s-flex-row e4s-justify-flex-row-vert-center">
        <div
          v-if="isTrack"
          class="e4s-flex-column e4s-justify-flex-end e4s-header--500 hybrid-start-race--first-col"
        >
          <div class="e4s-flex-row e4s-justify-flex-row-vert-center">
            <PulseIndicator
              color="red"
              :animate="false"
              class="e4s-flex-row--end"
              style="margin-right: var(--e4s-gap--standard)"
            />
          </div>
        </div>
        <div class="e4s-flex-row e4s-gap--standard e4s-justify-flex-row-vert-center">
          = DNS
        </div>
      </div>
    </div>

    <div class="e4s-vertical-spacer--standard"></div>
    <hr class="dat-e4s-hr-only dat-e4s-hr--light" />
    <HybridStartRace
      v-for="teamEntry in teams"
      :key="teamEntry.id"
      :show-position="false"
      :is-track="isTrack"
      urn=""
    >
      <template v-if="isTrack" #first-col>
        <div class="e4s-flex-row e4s-gap--small e4s-justify-flex-row-vert-center">
          <span class="e4s-lite-number e4s-lite-number--position-lane">{{
            getLaneNo(teamEntry)
          }}</span>
          <PulseIndicator
            v-if="teamEntry.present === 0"
            color="red"
            :animate="false"
            class="e4s-flex-row--end"
            style="margin-right: var(--e4s-gap--standard)"
          />
        </div>
      </template>
      <template #bibNo>
        {{ teamEntry.teamBibNo ? teamEntry.teamBibNo : '-' }}
      </template>
      <template #heatNo>
        {{ getHeatNo(teamEntry) }}
      </template>
      <template v-if="isTrack" #laneNo>
        {{ getLaneNo(teamEntry) }}
      </template>
      <template #athlete-name>
        {{ teamEntry.teamName }}
      </template>
      <template #name-sub>
        Athletes: {{ ResultsService.getTeamAthleteNamesString(teamEntry) }}
      </template>
    </HybridStartRace>
  </div>
  <!-- /SEEDED_TEAMS -->

  <!-- TBS_TEAMS -->
  <div class="e4s-flex-column e4s-lite--header-race-heat" v-if="toBeSeeded.length > 0">
    <div class="e4s-flex-row">
      <div v-if="isTrack ? true : howManyHeats > 1" class="e4s-header--400">
        <span v-text="isTrack ? 'Race' : 'Heat'"></span>
        TBS
      </div>
    </div>

    <div class="e4s-flex-row e4s-justify-flex-row-vert-center">
      <div
        v-if="isTrack"
        class="e4s-flex-column e4s-justify-flex-end e4s-header--500 hybrid-start-race--first-col"
      >
        <div class="e4s-flex-row e4s-justify-flex-row-vert-center">
          Lane
          <PulseIndicator
            color="red"
            :animate="false"
            class="e4s-flex-row--end"
            style="margin-right: var(--e4s-gap--standard)"
          />
        </div>
      </div>
      <div class="e4s-flex-row e4s-gap--standard e4s-justify-flex-row-vert-center">
        = DNS
      </div>
    </div>
  </div>
  <HybridStartRace
    v-for="teamEntry in toBeSeeded"
    :key="teamEntry.id"
    :show-position="false"
    :is-track="isTrack"
    urn=""
  >
    <template v-if="isTrack" #first-col>
      <div class="e4s-flex-row e4s-gap--small e4s-justify-flex-row-vert-center">
        <span class="e4s-lite-number e4s-lite-number--position-lane">TBS</span>
        <PulseIndicator
          v-if="teamEntry.present === 0"
          color="red"
          :animate="false"
          class="e4s-flex-row--end"
          style="margin-right: var(--e4s-gap--standard)"
        />
      </div>
    </template>
    <template #bibNo>
      {{ teamEntry.teamBibNo ? teamEntry.teamBibNo : '-' }}
    </template>
    <template #athlete-name>
      {{ teamEntry.teamName }}
    </template>
    <template #name-sub>
      Athletes: {{ ResultsService.getTeamAthleteNamesString(teamEntry) }}
    </template>
  </HybridStartRace>
  <!-- /TBS_TEAMS -->
</template>

<script setup lang="ts">
import type {
  CompEvent,
  HeatNoString,
  Seeding,
  StoreCompResultsState,
  Team
} from '@/components/results/store-comp-results'
import { computed, type PropType, ref, watch } from 'vue'
import * as ResultsService from '@/components/results/results-service'
import HybridStartRace from '@/components/results/HybridStartRace.vue'
import InputDebounce from '@/common/ui/fields/input-debounce.vue'
import PulseIndicator from '@/common/ui/icons/PulseIndicator.vue'
import { simpleClone } from '@/services/common-service-utils'

const props = defineProps({
  storeCompResultsState: {
    type: Object as PropType<StoreCompResultsState>,
    required: true
  },
  scheduledEvent: {
    type: Object as PropType<CompEvent>,
    required: true
  }
})

const filterTerm = ref('')
const teamsGroupedByHeatNo = ref<Record<HeatNoString, Team[]>>({})
const toBeSeeded = ref<Team[]>([])
const howManyHeats = ref(0)

filterTeams()

watch(
  () => props.scheduledEvent,
  () => {
    filterTerm.value = ''
    filterTeams()
  }
)

function filterTeams(searchTerm: string = '') {
  console.log('ResultTeamEntries.filterTeams searchTerm: ' + searchTerm)
  let compEvent = simpleClone(props.scheduledEvent)
  filterTerm.value = searchTerm

  if (searchTerm.length > 0) {
    compEvent = ResultsService.filterTeams(compEvent, searchTerm)
  }

  if (compEvent.teams && compEvent.teams.length > 0) {
    const result = ResultsService.getTeamsGroupedByHeatNo(compEvent)
    teamsGroupedByHeatNo.value = result.teamsGroupedByHeatNo
    toBeSeeded.value = result.toBeSeeded
    howManyHeats.value = Object.keys(result.teamsGroupedByHeatNo).length
  } else {
    teamsGroupedByHeatNo.value = {}
    toBeSeeded.value = []
    howManyHeats.value = 0
  }
}

const isTrack = computed(() => {
  return ResultsService.isTrackEvent(props.scheduledEvent)
})

function getHeatNo(team: Team): number | string {
  const seeding = team.seeding as Seeding | undefined
  return seeding ? seeding.heatNo : ''
}

function getLaneNo(team: Team): number | string {
  const seeding = team.seeding as Seeding | undefined
  return seeding && seeding.laneNo > 0 ? seeding.laneNo : 'TBS'
}
</script>

<style scoped>
.hybrid-start-race--bib-no {
  width: 50px;
}

.hybrid-start-race--po10-pb-sb {
  width: 50px;
}
</style>
